/* eslint-disable no-unused-vars */
import { connect, useDispatch } from 'react-redux';
import { enums, IPriceAlert, ITicker } from '@kattana-io/common';
import DesktopTable from '../../../molecules/Table/DesktopTable';
import Badge from '../../../molecules/Badge';
import PriceAlertsActions from './Actions';
import Text from '../../../atoms/Text';
import TickerCell from '../TickerCell';
import { useTableData } from './useTableData';
import { actionShowModal } from '../../../actions/modal';
import { ModalTypes } from '../../../reducers/modal';
import { toFixed2 } from '../formatters';
import { IStore } from '../../../reducers';
import { IAuthState } from '../../../reducers/auth';
import { decoratePriceAlertStatus } from '../../../helpers/decorators';

export const LS_PRICE_ALERTS_HIDDEN_COLUMNS_KEY =
  'kattana-simple.price_alerts_table_columns_hidden';
export const LS_PRICE_ALERTS_KEY = 'kattana-simple.price_alerts_table_columns';

type tickersMap = Record<string, ITicker>;

interface IPriceAlertsTableProps {
  tickers: tickersMap;
  alerts: any[];
  loading: boolean;
  filter: string;
  auth: IAuthState;
  onFilter(filter: string): void;
  width?: number;
}

/**
 * Return humanized condition of price alert
 * @param param0
 * @returns
 */
const priceDisplayFormatter = ({ gt, price, price_usd }: IPriceAlert) =>
  price_usd ? `${price_usd} $` : `${price}`;

const decorateDiff = (B: number, A: number, usd?: boolean): string => {
  const diff = B - A;
  const diffPercent = (diff / B) * 100;

  if (isFinite(diffPercent)) {
    return `(${toFixed2(diffPercent)}%) ${
      usd ? `${toFixed2(diff)} $` : toFixed2(diff)
    }`;
  }

  return `${toFixed2(diff)}`;
};

/**
 * Return humanized condition of price alert
 * @param param0
 * @returns
 */
const priceDifferenceFormatter = (
  { gt, price, price_usd }: IPriceAlert,
  ticker: ITicker
): string => {
  if (ticker) {
    if (price_usd) {
      const target = ticker.flipped ? ticker.price_b_usd : ticker.price_a_usd;
      return decorateDiff(price_usd, target, true);
    }
    const target = ticker.flipped ? ticker.price_b : ticker.price_a;
    return decorateDiff(price, target);
  } else {
    return price_usd ? `${price_usd} $` : `${price}`;
  }
};

const statusToBadgeKlass = (status: string): any => {
  switch (status) {
  case enums.PriceAlertStatus.WATCH:
    return 'green';
  case enums.PriceAlertStatus.CANCELED:
    return 'red';
  default:
    return 'grey';
  }
};

const getClassNameForRow = (row: any) => {
  if (row.status === enums.PriceAlertStatus.WATCH) {
    return 'row--Buy text--green-600';
  }
  return 'row--Both text--grey-600';
};

/**
 * Block variable will trigger rerender on each block of this net
 * @param param0
 * @returns
 */
const PriceAlertsTable = ({
  tickers,
  alerts,
  filter,
  onFilter,
  width,
  loading,
  auth
}: IPriceAlertsTableProps) => {
  const dispatch = useDispatch();
  const placeholder = auth.loggedIn ? 'No data found' : 'Connect wallet to get access to price alerts';

  const onPriceEdit = (alert: IPriceAlert) => {
    if (alert.status === enums.PriceAlertStatus.WATCH) {
      dispatch(actionShowModal(ModalTypes.priceAlertEdit, { alert }));
    }
  };

  const { columns, data, tableColumnsWidth } = useTableData({
    alerts,
  });

  const renderCell = (cell: any) => {
    const { chain, pair } = cell.row.original;
    const key = `${chain}/${pair}`;
    const ticker = tickers[key];

    switch (cell.column.id) {
    case 'ticker':
      return <TickerCell path={key} ticker={ticker} />;
    case 'alertPrice':
      return (
        <Text size={12} onClick={() => onPriceEdit(cell.row.original)}>
          {priceDisplayFormatter(cell.row.original)}
        </Text>
      );
    case 'difference':
      return (
        <Text size={12}>
          {priceDifferenceFormatter(cell.row.original, ticker)}
        </Text>
      );
    case 'actions':
      return (
        <PriceAlertsActions
          alert={cell.row.original}
          filterEnabled={key === filter}
          onFilter={onFilter}
        />
      );
    case 'status':
      return (
        <Badge
          variant={'fill'}
          klass={statusToBadgeKlass(cell.value)}
          text={decoratePriceAlertStatus(cell.value)}
        />
      );
    default:
      return <Text size={12}>{cell.value}</Text>;
    }
  };

  return (
    <DesktopTable
      hiddenColumnsKey={LS_PRICE_ALERTS_HIDDEN_COLUMNS_KEY}
      dataKey={LS_PRICE_ALERTS_KEY}
      title='price_alerts'
      tableColumnsWidth={tableColumnsWidth}
      tableWidth={width || 0}
      columns={columns}
      data={data}
      getClassNameForRow={getClassNameForRow}
      renderCell={renderCell}
      loading={loading}
      placeholderText={placeholder}
    />
  );
};

const mapStateToProps = (state: IStore) => ({
  auth: state.auth,
});

export default connect(mapStateToProps, {})(PriceAlertsTable);
